import React, { useState, useEffect, useRef } from 'react';

const BUENOS_AIRES_COORDS = { lat: -34.6037, lng: -58.3816 };

const LocationPickerMap = ({ initialCoordinates, onCoordinatesChange }) => {
  const [map, setMap] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [tempCoordinates, setTempCoordinates] = useState(initialCoordinates);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);

  // Sincronizar estado interno cuando cambian las props externamente
  useEffect(() => {
    if (initialCoordinates) {
      setTempCoordinates(initialCoordinates);
    }
  }, [initialCoordinates]);

  // Inicialización del Mapa
  useEffect(() => {
    if (window.google && !map) {
      const mapInstance = new window.google.maps.Map(document.getElementById('picker-map'), {
        center: (initialCoordinates && typeof initialCoordinates.lat === 'number') ? initialCoordinates : BUENOS_AIRES_COORDS,
        zoom: 12,
        mapTypeId: 'hybrid',
      });
      setMap(mapInstance);
      setAutocompleteService(new window.google.maps.places.AutocompleteService());
      setPlacesService(new window.google.maps.places.PlacesService(mapInstance));
    }
  }, [map]);

  // Manejo del Marcador y panTo
  useEffect(() => {
    if (!map || !window.google) return;

    const isValid = (c) => c && typeof c.lat === 'number' && !isNaN(c.lat) && typeof c.lng === 'number' && !isNaN(c.lng);
    
    const markerPosition = isValid(tempCoordinates) ? tempCoordinates : 
                           isValid(initialCoordinates) ? initialCoordinates : 
                           BUENOS_AIRES_COORDS;

    // Si por alguna razón los números no son finitos, abortamos para no romper Google Maps
    if (!Number.isFinite(markerPosition.lat) || !Number.isFinite(markerPosition.lng)) return;

    if (activeMarker) activeMarker.setMap(null);

    const marker = new window.google.maps.Marker({
      position: markerPosition,
      map: map,
      draggable: true,
      animation: window.google.maps.Animation.DROP
    });

    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      const newCoords = { lat: position.lat(), lng: position.lng() };
      setTempCoordinates(newCoords);
      onCoordinatesChange(newCoords);
    });

    setActiveMarker(marker);
    map.panTo(markerPosition);
    
    // Cleanup al desmontar
    return () => {
      marker.setMap(null);
    };
    // No incluimos onCoordinatesChange aquí para evitar loops infinitos
  }, [map, initialCoordinates]);

  const handleSearch = () => {
    if (!searchQuery || !autocompleteService) return;
    autocompleteService.getPlacePredictions({
      input: searchQuery,
      location: new window.google.maps.LatLng(BUENOS_AIRES_COORDS),
      radius: 50000,
    }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSearchResults(predictions || []);
      }
    });
  };

  const selectSearchResult = (placeId) => {
    if (!placesService || !map) return;
    placesService.getDetails({ placeId, fields: ['geometry'] }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setTempCoordinates(coords);
        onCoordinatesChange(coords);
        map.panTo(coords);
        map.setZoom(17);
      }
    });
    setSearchResults([]);
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-3 border-b bg-gray-50">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); handleSearch(); }}
            placeholder="Buscar dirección..."
            className="w-full px-3 py-2 text-sm border rounded-md outline-none focus:ring-2 focus:ring-blue-400"
          />
          {searchResults.length > 0 && (
            <div className="absolute w-full mt-1 bg-white border shadow-2xl z-[100] max-h-40 overflow-y-auto">
              {searchResults.map((r) => (
                <div 
                  key={r.place_id} 
                  onClick={() => selectSearchResult(r.place_id)}
                  className="p-2 text-xs hover:bg-blue-50 cursor-pointer border-b last:border-0"
                >
                  {r.description}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div id="picker-map" className="flex-grow w-full h-full" style={{ minHeight: '300px' }} />
    </div>
  );
};

export default LocationPickerMap;